import {
  keepPreviousData,
  QueryClient,
  queryOptions,
  type QueryClientConfig,
  type QueryKey,
} from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";

import type { ApiClient, Versioned } from "./client";
import { ApiError } from "./errors";
import { listQueryKey, type ListQuery, type PagedResult } from "./listQuery";

const ApiClientContext = createContext<ApiClient | null>(null);

/** Makes the API client available to hooks and floorplans. */
export function ApiClientProvider({ client, children }: { client: ApiClient; children: ReactNode }) {
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (!client) {
    throw new Error("useApiClient must be used inside ApiClientProvider");
  }
  return client;
}

/**
 * Retry policy: client errors (4xx) are final — retrying a validation failure, a conflict or a 403 cannot succeed.
 * Network errors and 5xx are retried twice.
 */
// eslint-disable-next-line react-refresh/only-export-components -- plain helper
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

/** Query client with the platform defaults. */
// eslint-disable-next-line react-refresh/only-export-components -- plain helper
export function createQueryClient(config: QueryClientConfig = {}): QueryClient {
  return new QueryClient({
    ...config,
    defaultOptions: {
      ...config.defaultOptions,
      queries: {
        staleTime: 30_000,
        retry: shouldRetry,
        refetchOnWindowFocus: false,
        ...config.defaultOptions?.queries,
      },
      mutations: { retry: false, ...config.defaultOptions?.mutations },
    },
  });
}

/**
 * Query options for a server-side paged list. The key includes the full list query so each page/sort/filter
 * combination is cached separately; the previous page stays visible while the next one loads.
 */
// eslint-disable-next-line react-refresh/only-export-components -- plain helper
export function listQueryOptions<T>(client: ApiClient, resourceKey: QueryKey, path: string, query: ListQuery) {
  return queryOptions<PagedResult<T>>({
    queryKey: [...resourceKey, ...listQueryKey(query)],
    queryFn: ({ signal }) => client.list<T>(path, query, { signal }),
    placeholderData: keepPreviousData,
  });
}

/** Query options for a single record read with its row version (edit screens, ADR-0010/D-7). */
// eslint-disable-next-line react-refresh/only-export-components -- plain helper
export function versionedQueryOptions<T>(client: ApiClient, queryKey: QueryKey, path: string) {
  return queryOptions<Versioned<T>>({
    queryKey,
    queryFn: ({ signal }) => client.getVersioned<T>(path, { signal }),
  });
}

export interface SaveVariables<T> {
  data: T;
  /** Row version the user started editing from; sent as `If-Match`. */
  version: string | null;
}

/**
 * Mutation function for an optimistic-concurrency update: `PUT` with `If-Match`; a stale version rejects with a
 * `ConflictError` that the Object Page turns into the conflict dialog.
 */
// eslint-disable-next-line react-refresh/only-export-components -- plain helper
export function versionedSave<T, R = T>(client: ApiClient, path: string) {
  return async ({ data, version }: SaveVariables<T>): Promise<Versioned<R>> => {
    const response = await client.put<R>(path, data, { ifMatch: version });
    return { data: response.data, version: response.version };
  };
}
